/**
 * Gráfico de barras — faturamento mensal do ano (realizado + projeção).
 */

import React, { useMemo, useState } from 'react'
import { BarChart3 } from 'lucide-react'
import { formatCurrencyBRL } from '../../../../utils/formatCurrency'
import './MonthlyRevenueBarChart.css'

function formatCurrency(value) {
  return formatCurrencyBRL(value)
}

function MonthlyRevenueBarChart({ data, formatValue = formatCurrency }) {
  const [hoveredIndex, setHoveredIndex] = useState(null)

  const chart = useMemo(() => {
    if (!data?.months?.length) return null

    const months = data.months
    const maxRevenue = Math.max(...months.map((m) => m.revenue || 0), 1)

    return {
      months,
      maxRevenue,
      bars: months.map((m, index) => ({
        ...m,
        index,
        heightPercent: ((m.revenue || 0) / maxRevenue) * 100
      }))
    }
  }, [data])

  if (!chart) {
    return (
      <div className="erp-monthly-revenue-chart erp-monthly-revenue-chart--empty">
        <p>Sem dados de faturamento mensal</p>
      </div>
    )
  }

  const hovered = hoveredIndex !== null ? chart.bars[hoveredIndex] : null

  return (
    <div className="erp-monthly-revenue-chart">
      <div className="erp-monthly-revenue-chart-header">
        <div>
          <h3 className="erp-monthly-revenue-chart-title">
            <BarChart3 size={16} className="erp-monthly-revenue-chart-title-icon" />
            Faturamento Mensal {data.year}
          </h3>
          <p className="erp-monthly-revenue-chart-subtitle">
            Meses futuros projetados pela média dos meses já encerrados
          </p>
        </div>
        <div className="erp-monthly-revenue-chart-legend">
          <span className="erp-monthly-revenue-legend-item">
            <span className="erp-monthly-revenue-legend-swatch realized" />
            Realizado
          </span>
          <span className="erp-monthly-revenue-legend-item">
            <span className="erp-monthly-revenue-legend-swatch projected" />
            Projetado
          </span>
        </div>
      </div>

      <div className="erp-monthly-revenue-chart-summary">
        <div>
          <p className="erp-monthly-revenue-summary-label">Realizado no ano</p>
          <p className="erp-monthly-revenue-summary-value">{formatValue(data.realizedTotal)}</p>
        </div>
        <div>
          <p className="erp-monthly-revenue-summary-label">Projeção restante</p>
          <p className="erp-monthly-revenue-summary-value muted">{formatValue(data.projectedTotal)}</p>
        </div>
        <div>
          <p className="erp-monthly-revenue-summary-label">Previsão anual</p>
          <p className="erp-monthly-revenue-summary-value highlight">{formatValue(data.yearForecast)}</p>
        </div>
      </div>

      <div className="erp-monthly-revenue-chart-body">
        <div className="erp-monthly-revenue-bars">
          {chart.bars.map((bar) => (
            <div
              key={bar.month}
              className={`erp-monthly-revenue-bar-col${bar.isCurrent ? ' is-current' : ''}${hoveredIndex === bar.index ? ' is-hovered' : ''}`}
              onMouseEnter={() => setHoveredIndex(bar.index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="erp-monthly-revenue-bar-track">
                <div
                  className={`erp-monthly-revenue-bar-fill${bar.isProjected ? ' projected' : ' realized'}`}
                  style={{ height: `${Math.max(bar.heightPercent, bar.revenue > 0 ? 4 : 0)}%` }}
                  title={`${bar.label}: ${formatValue(bar.revenue)}`}
                />
              </div>
              <span className="erp-monthly-revenue-bar-label">{bar.label}</span>
            </div>
          ))}
        </div>

        {hovered && (
          <div className="erp-monthly-revenue-tooltip">
            <p className="erp-monthly-revenue-tooltip-title">
              {hovered.label} {data.year}
              {hovered.isCurrent && ' (mês atual)'}
            </p>
            <p className="erp-monthly-revenue-tooltip-value">{formatValue(hovered.revenue)}</p>
            <p className="erp-monthly-revenue-tooltip-meta">
              {hovered.isProjected ? 'Valor projetado' : 'Faturamento realizado'}
              {hovered.orderCount > 0 && ` · ${hovered.orderCount} pedidos`}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default MonthlyRevenueBarChart
